/* Checkout button state management with terms checkbox
 * Handles minimum order requirement and terms acceptance
 */

if (typeof TermsAndConditionsToggle !== 'function') {

  class TermsAndConditionsToggle extends HTMLElement {
    constructor() {
      super();
      this.isSetup = false;
      this.checkbox = null;
      this.checkoutButton = null;

      // Delay initialization to avoid conflicts
      requestAnimationFrame(() => {
        this.init();
      });
    }

    init() {
      if (this.isSetup) return;

      this.checkbox = this.querySelector('#AgreeToTos');
      this.checkoutButton = document.querySelector('#CheckOut');

      // Force checkbox unchecked on initialization
      if (this.checkbox) {
        this.checkbox.checked = false;
      }

      if (this.checkoutButton) {
        this.setupButton();
      } else {
        // Wait for button with MutationObserver
        this.observeForButton();
      }
    }

    observeForButton() {
      const observer = new MutationObserver((mutations, obs) => {
        this.checkoutButton = document.querySelector('#CheckOut');
        if (this.checkoutButton) {
          obs.disconnect();
          this.setupButton();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    setupButton() {
      if (this.isSetup || !this.checkoutButton) return;
      this.isSetup = true;

      // Force button disabled initially
      this.updateButtonState();

      // Setup event listeners
      if (this.checkbox) {
        this.checkbox.addEventListener('change', () => {
          this.updateButtonState();
        });
      }

      // Listen to cart updates - use a single handler
      const handleCartUpdate = () => {
        // Re-query elements in case they were replaced by AJAX
        this.checkbox = this.querySelector('#AgreeToTos');
        this.checkoutButton = document.querySelector('#CheckOut');

        if (this.checkbox) {
          this.checkbox.checked = false;
        }

        this.updateButtonState();
      };

      // Add listeners only once
      document.addEventListener('cart-updated', handleCartUpdate);
      document.addEventListener('cart:updated', handleCartUpdate);
    }

    updateButtonState() {
      if (!this.checkoutButton) return;

      const checkbox = document.querySelector('#AgreeToTos');
      const requiresTerms = this.checkoutButton.dataset.requiresTerms === 'true';
      const minimumReached = this.checkoutButton.dataset.minimumReached === 'true';
      const termsAccepted = checkbox ? checkbox.checked : false;

      // Calculate if button should be disabled
      let shouldDisable = !minimumReached;
      if (requiresTerms && minimumReached) {
        shouldDisable = !termsAccepted;
      }

      // Update button state
      this.checkoutButton.disabled = shouldDisable;

      if (shouldDisable) {
        this.checkoutButton.setAttribute('disabled', 'disabled');
      } else {
        this.checkoutButton.removeAttribute('disabled');
      }
    }
  }

  // Define custom element
  if (typeof customElements.get('terms-checkbox') === 'undefined') {
    customElements.define('terms-checkbox', TermsAndConditionsToggle);
  }
}

// AGGRESSIVE ENFORCEMENT - Overrides all other scripts
(function() {
  // Track if already initialized to prevent duplicates
  if (window.__checkoutButtonInitialized) return;
  window.__checkoutButtonInitialized = true;

  let enforcementActive = true;

  function updateCheckoutButton(source = '') {
    if (!enforcementActive) return;

    const button = document.querySelector('#CheckOut');
    const checkbox = document.querySelector('#AgreeToTos');

    if (!button) return;

    const requiresTerms = button.dataset.requiresTerms === 'true';
    const minimumReached = button.dataset.minimumReached === 'true';
    const termsAccepted = checkbox ? checkbox.checked : false;

    // Determine if button should be disabled
    let shouldDisable = !minimumReached;
    if (requiresTerms && minimumReached) {
      shouldDisable = !termsAccepted;
    }

    // Only update if state has changed to avoid infinite loops
    if (button.disabled !== shouldDisable) {
      button.disabled = shouldDisable;

      if (shouldDisable) {
        button.setAttribute('disabled', 'disabled');
      } else {
        button.removeAttribute('disabled');
      }
    }
  }

  // Override the disabled property setter to intercept all changes
  function protectButton() {
    const button = document.querySelector('#CheckOut');
    if (!button) return;

    // Store original property descriptor
    const originalDescriptor = Object.getOwnPropertyDescriptor(HTMLButtonElement.prototype, 'disabled');

    // Define new property for this specific button
    Object.defineProperty(button, 'disabled', {
      get() {
        return originalDescriptor.get.call(this);
      },
      set(value) {
        // Always run our check when someone tries to change disabled state
        originalDescriptor.set.call(this, value);

        // Immediately re-check the correct state
        setTimeout(() => {
          if (enforcementActive) {
            updateCheckoutButton('property-setter');
          }
        }, 0);
      },
      configurable: true
    });
  }

  // Initialize when DOM is ready
  function init() {
    updateCheckoutButton('init');
    protectButton();

    // Listen for checkbox changes
    const checkbox = document.querySelector('#AgreeToTos');
    if (checkbox) {
      checkbox.addEventListener('change', () => updateCheckoutButton('checkbox-change'));
    }

    // Listen for cart updates
    document.addEventListener('cart-updated', () => {
      const cb = document.querySelector('#AgreeToTos');
      if (cb) cb.checked = false;
      updateCheckoutButton('cart-updated');
    });

    document.addEventListener('cart:updated', () => {
      const cb = document.querySelector('#AgreeToTos');
      if (cb) cb.checked = false;
      updateCheckoutButton('cart:updated');
    });

    // Monitor button for attribute changes with MutationObserver
    const button = document.querySelector('#CheckOut');
    if (button) {
      const observer = new MutationObserver((mutations) => {
        // Check if disabled attribute was changed
        for (const mutation of mutations) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'disabled') {
            updateCheckoutButton('mutation-observer');
            break;
          }
        }
      });

      observer.observe(button, {
        attributes: true,
        attributeFilter: ['disabled', 'data-minimum-reached', 'data-requires-terms']
      });
    }

    // Enforcement for first 3 seconds to catch late scripts
    let enforceCount = 0;
    const enforceInterval = setInterval(() => {
      updateCheckoutButton('interval-' + enforceCount);
      protectButton(); // Re-apply protection in case button was replaced
      enforceCount++;
      if (enforceCount >= 30) { // 30 * 100ms = 3 seconds
        clearInterval(enforceInterval);
        // Continue with less frequent checks
        setInterval(() => {
          updateCheckoutButton('long-interval');
        }, 1000);
      }
    }, 100);
  }

  // Multiple initialization attempts to ensure we catch the button
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    requestAnimationFrame(init);
  }

  // Backup initialization attempts
  setTimeout(init, 100);
  setTimeout(init, 500);
  setTimeout(init, 1000);

  // Re-run when page becomes visible (tab switching)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      updateCheckoutButton('visibility-change');
    }
  });

  // Expose function to window for debugging
  window.__debugCheckoutButton = () => {
    const button = document.querySelector('#CheckOut');
    const checkbox = document.querySelector('#AgreeToTos');
    console.log('Button state:', {
      disabled: button?.disabled,
      hasDisabledAttr: button?.hasAttribute('disabled'),
      minimumReached: button?.dataset.minimumReached,
      requiresTerms: button?.dataset.requiresTerms,
      termsChecked: checkbox?.checked,
      enforcementActive
    });
  };

  window.__toggleEnforcement = () => {
    enforcementActive = !enforcementActive;
    console.log('Enforcement is now:', enforcementActive ? 'ACTIVE' : 'INACTIVE');
  };
})();